'use client'

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrashIcon,
} from '@heroicons/react/24/outline';
import { primaryDebtor as getPrimaryDebtor } from '../utils/debtors';
import DashboardShell from './components/DashboardShell';
const API_URL = process.env.REACT_APP_API_URL;

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}
const SORT_LABELS = {
  created_at: 'Created Date',
  updated_at: 'Updated Date',
  meeting_date: 'Meeting Date',
  reaffirmation_deadline: 'Reaffirmation Deadline',
  date_filed: 'Date Filed',
  financial_management_deadline: 'Claims Deadline',
};
export default function FirmDashboard() {
  const navigate = useNavigate();
  const [deletingCaseIds, setDeletingCaseIds] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [cases, setCases] = useState([])
  const [loadingCases, setLoadingCases] = useState(true);
  const [loadError, setLoadError] = useState('');

  const tabs = ['prospect', 'retained', 'filed'];
  const [activeTab, setActiveTab] = useState('prospect');
  const [sortKey, setSortKey] = useState('created_at');
const [sortDirection, setSortDirection] = useState('desc');
const [chapterFilter, setChapterFilter] = useState('all');
const today = new Date();
  const getActiveFilterLabels = () => {
  const labels = [];

  if (chapterFilter !== 'all') {
    labels.push(`Chapter ${chapterFilter}`);
  }

  if (activeTab === 'filed') {
    if (filedFilters.courtFeesDue) labels.push('Court fees due');
    if (filedFilters.fmNeedsUploaded) labels.push('FM needs upload');
    if (filedFilters.fmNeedsFiled) labels.push('FM needs filed');
    if (filedFilters.fmDeadlinePast) labels.push('FM deadline past');
    if (filedFilters.trusteeDocsMissing) labels.push('Trustee docs missing');
  }

  if (activeTab === 'retained') {
    if (retainedFilters.needsReview) labels.push('Needs review');
  }

  return labels;
};
  // Filters
  const [filedFilters, setFiledFilters] = useState({
    courtFeesDue: false,
    fmNeedsUploaded: false,
    fmNeedsFiled: false,
    fmDeadlinePast: false,
    trusteeDocsMissing: false,
  });
  const [retainedFilters, setRetainedFilters] = useState({
    needsReview: false,
  });
const caseHasCourtFeesDue = (c) => {
  const paid = Number(c?.filing_fees_paid || 0);
  const receipted = Number(c?.filing_fees_receipted || 0);
  // Fees due to court when paid > receipted (client paid you, but court not yet receipted)
  return paid - receipted > 0.009;
};

  const caseFinancialManagementNeedsUploaded = (c) => {
    if (c?.financial_management_course_filed) return false;
    return !c?.fm_has_usable_upload;
  };

  const caseFinancialManagementNeedsFiled = (c) => {
    if (c?.financial_management_course_filed) return false;
    return !!c?.fm_has_usable_upload;
  };

  const caseFinancialManagementDeadlinePast = (c) => {
    if (!c?.financial_management_deadline) return false;
    const d = new Date(c.financial_management_deadline);
    return d < today;
  };

  const caseTrusteeDocsMissing = (c) => !c?.trustee_docs;

  const caseNeedsDocReview = (c) => {
    return Number(c?.document_summary?.awaiting_review || 0) > 0;
  };
  // Many backend fields are date-only strings like "YYYY-MM-DD".
  // `new Date('YYYY-MM-DD')` is parsed as UTC, which can display as the prior day in US timezones.
  // Parse these as local dates instead.
  const parseLocalDate = (value) => {
    if (!value) return null;
    // If it already includes a time, let Date handle it.
    if (typeof value === 'string' && value.includes('T')) return new Date(value);

    const s = String(value);
    const parts = s.split('-');
    if (parts.length >= 3) {
      const y = Number(parts[0]);
      const m = Number(parts[1]);
      const d = Number(parts[2]);
      if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
        return new Date(y, m - 1, d); // local midnight
      }
    }
    // Fallback
    return new Date(value);
  };
  useEffect(() => {
    const controller = new AbortController();
    const fetchCases = async () => {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams({
        status: activeTab,
        page_size: '100',
        sort: sortKey,
        direction: sortDirection,
      });
      if (chapterFilter !== 'all') params.set('chapter', chapterFilter);
      if (activeTab === 'filed') {
        if (filedFilters.courtFeesDue) params.set('court_fees_due', 'true');
        if (filedFilters.fmNeedsUploaded) params.set('fm_needs_uploaded', 'true');
        if (filedFilters.fmNeedsFiled) params.set('fm_needs_filed', 'true');
        if (filedFilters.fmDeadlinePast) params.set('fm_deadline_past', 'true');
        if (filedFilters.trusteeDocsMissing) params.set('trustee_docs_missing', 'true');
      }
      if (activeTab === 'retained' && retainedFilters.needsReview) {
        params.set('needs_review', 'true');
      }

      setLoadingCases(true);
      setLoadError('');
      try {
        const fetchPage = async (pageNumber) => {
          const pageParams = new URLSearchParams(params);
          pageParams.set('page', String(pageNumber));
          const response = await fetch(`${API_URL}/firm-cases/?${pageParams.toString()}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
          });
          if (!response.ok) throw new Error(`Failed to load cases (${response.status})`);
          return response.json();
        };

        const firstPage = await fetchPage(1);
        if (Array.isArray(firstPage)) {
          setCases(firstPage);
          return;
        }

        const allCases = [...(firstPage.results || [])];
        const totalPages = Number(firstPage.total_pages || 1);
        // Load every remaining lightweight summary page in small groups. This keeps
        // the API protected from unbounded responses without limiting the dashboard.
        for (let first = 2; first <= totalPages; first += 4) {
          const pageNumbers = Array.from(
            { length: Math.min(4, totalPages - first + 1) },
            (_, index) => first + index,
          );
          const pages = await Promise.all(pageNumbers.map(fetchPage));
          pages.forEach(pageData => allCases.push(...(pageData.results || [])));
        }
        setCases(allCases);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Failed to fetch cases', err);
        setCases([]);
        setLoadError('Cases could not be loaded. Please try again.');
      } finally {
        if (!controller.signal.aborted) setLoadingCases(false);
      }
    };
    fetchCases();
    return () => controller.abort();
  }, [
    activeTab,
    sortKey,
    sortDirection,
    chapterFilter,
    filedFilters,
    retainedFilters,
  ]);

  const handleDeleteCase = async (caseId) => {
    const token = localStorage.getItem('accessToken');
    if (!window.confirm('Are you sure you want to delete this case?')) {
      return;
    }
    setDeletingCaseIds(prev => [...prev, caseId]);
    try {
      const res = await fetch(`${API_URL}/firm-cases/${caseId}/`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        setCases(prevCases => prevCases.filter(c => c.case_id !== caseId));
        setSuccessMessage('Case deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 2000); // Message disappears after 3 seconds
      } else {
        console.error('Failed to delete case');
      }
    } catch (err) {
      console.error('Error deleting case', err);
    } finally {
      setDeletingCaseIds(prev => prev.filter(id => id !== caseId));
    }
  }

  const renderCases = () => {
    const status = activeTab;
    let filtered = [...cases].filter(c => c.status === status);
    if (chapterFilter !== 'all') {
  filtered = filtered.filter(c => String(c.chapter) === String(chapterFilter));
}
    // Apply tab-specific filters
    if (status === 'filed') {
      filtered = filtered.filter(c => {
        if (filedFilters.courtFeesDue && !caseHasCourtFeesDue(c)) return false;
        if (filedFilters.fmNeedsUploaded && !caseFinancialManagementNeedsUploaded(c)) return false;
        if (filedFilters.fmNeedsFiled && !caseFinancialManagementNeedsFiled(c)) return false;
        if (filedFilters.fmDeadlinePast && !caseFinancialManagementDeadlinePast(c)) return false;
        if (filedFilters.trusteeDocsMissing && !caseTrusteeDocsMissing(c)) return false;
        return true;
      });
    }

    if (status === 'retained') {
      filtered = filtered.filter(c => {
        if (retainedFilters.needsReview && !caseNeedsDocReview(c)) return false;
        return true;
      });
    }
    filtered.sort((a, b) => {
      if (sortKey === 'chapter') return a.chapter.localeCompare(b.chapter);
      if (['created_at','updated_at','meeting_date','date_filed','reaffirmation_deadline','financial_management_deadline'].includes(sortKey)) {
        // Some fields may be undefined/null; handle gracefully
        const aVal = a[sortKey] ? new Date(a[sortKey]) : new Date(0);
        const bVal = b[sortKey] ? new Date(b[sortKey]) : new Date(0);
        const diff = aVal - bVal;
        return sortDirection === 'asc' ? diff : -diff;
      }
      return 0;
    });
    const chapters = ['7', '13'];
    return (
      <div className="bg-white rounded shadow p-6 mb-8">
        <h3 className="text-lg font-semibold mb-4">
          {status.charAt(0).toUpperCase() + status.slice(1)} Cases
        </h3>
        {chapters.map(chap => {
          const chapterCases = filtered.filter(c => c.chapter === chap);
          if (!chapterCases.length) return null;
          if (status === 'filed') {
            const sortedChapterCases = chapterCases;
            // Dynamic divider logic for date fields only
            let dividerIndex = -1;
            // Updated dateFields array
            const dateFields = ['created_at', 'updated_at', 'meeting_date', 'date_filed', 'reaffirmation_deadline', 'financial_management_deadline'];
            if (dateFields.includes(sortKey)) {
              if (sortDirection === 'asc') {
                dividerIndex = sortedChapterCases.findIndex(c => {
                  const dateVal = c[sortKey] ? new Date(c[sortKey]) : new Date(0);
                  return dateVal >= today;
                });
              } else {
                dividerIndex = sortedChapterCases.findIndex(c => {
                  const dateVal = c[sortKey] ? new Date(c[sortKey]) : new Date(0);
                  return dateVal <= today;
                });
              }
            }
            // Merge in a "Today" card at the correct sort position
            const mergedCases = [...sortedChapterCases];
            const todayCard = { isToday: true };
            const insertIndex = dividerIndex >= 0 ? dividerIndex : mergedCases.length;
            mergedCases.splice(insertIndex, 0, todayCard);
            return (
              <div key={chap} className="mt-4">
                <h4 className="text-md font-semibold mb-2">Chapter {chap}</h4>
                <ul className="space-y-4">
                  {mergedCases.map((c, i) => {
                    if (c.isToday) {
                      let nextItem = mergedCases[i - 1];
                      if (sortDirection === "asc") nextItem = mergedCases[i + 1];
                      const nextDate = nextItem && nextItem[sortKey] ? new Date(nextItem[sortKey]) : null;
                      const nextLabel = sortKey.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                      const daysDiff = nextDate ? Math.ceil((nextDate - today) / (1000 * 60 * 60 * 24)) : null;

                      return (
                        <li key={`today-${chap}`} className="py-4 px-4 border border-gray-200 rounded-lg mb-4">
                          <div className="p-4 bg-indigo-50 border-l-4 border-indigo-500 rounded text-center">
                            <div className="font-semibold text-indigo-700">
                              Today: {today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </div>
                            {nextDate && (
                              <div className="text-sm text-gray-700 mt-2">
                                Next {nextLabel}: {nextDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (in {daysDiff} days)
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    }
                    const primaryDebtor = getPrimaryDebtor(c.debtors, { filingOnly: true });
                    const fmFiled = !!c.financial_management_course_filed;
                    const fmItemExists = !!c.fm_checklist_exists;
                    const fmHasUpload = !!c.fm_has_usable_upload;
                    const fmNeedsFiled = !fmFiled;
                    const fmStatus = fmFiled ? 'filed' : (fmHasUpload ? 'uploaded' : 'needed');

                    const fmDeadline = c.financial_management_deadline ? new Date(c.financial_management_deadline) : null;
                    const fmPastDue = fmNeedsFiled && fmDeadline && fmDeadline < today;
                    return (
                      <li
                        key={c.case_id}
                        className={`relative group py-4 px-4 border border-gray-200 rounded-lg flex justify-between items-center transition-transform duration-200 ease-in-out hover:scale-105 hover:bg-gray-50 cursor-pointer ${
                          deletingCaseIds.includes(c.case_id) ? 'opacity-50 pointer-events-none' : ''
                        }`}
                      >
                        {/* Full-card overlay link enables middle/cmd/ctrl click */}
                        <a
                          href={`/case/${c.case_id}`}
                          className="absolute inset-0 z-0"
                          aria-label={`Open case ${c.case_id}`}
                          onClick={(e) => {
                            // Let modifier-clicks open a real new tab/window
                            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                            e.preventDefault();
                            navigate(`/case/${c.case_id}`);
                          }}
                        />
                        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* First column: main info and fees */}
                          <div className="flex flex-col">
                            {deletingCaseIds.includes(c.case_id) && (
                              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                                <svg
                                  className="animate-spin h-6 w-6 text-gray-600"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z"
                                  ></path>
                                </svg>
                              </div>
                            )}
                            <div className="font-semibold text-gray-900">
                              {primaryDebtor
                                ? `${primaryDebtor.first_name} ${primaryDebtor.second_name ? primaryDebtor.second_name + ' ' : ''}${primaryDebtor.last_name}`
                                : 'Unnamed Client'}
                            </div>
                            <div className="text-sm text-gray-600">
                              Case ID: {c.case_id}
                            </div>
                            <div className="text-sm text-gray-600">
                              Case Number: {c.case_number || 'TBD'}
                            </div>
                            <div className="text-sm text-gray-600">
                              Email: {primaryDebtor?.email || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-600">
                              Phone: {primaryDebtor?.phone || 'N/A'}
                            </div>
                            <div className="mt-2">
                              {fmStatus === 'filed' ? (
                                <div className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800 ring-1 ring-inset ring-green-200">
                                  Financial Management: Filed ✓
                                </div>
                              ) : fmStatus === 'uploaded' ? (
                                <div className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800 ring-1 ring-inset ring-red-200">
                                  Financial Management: Uploaded (still needs filed)
                                </div>
                              ) : (
                                <div className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-semibold text-yellow-800 ring-1 ring-inset ring-yellow-200">
                                  Financial Management: Needed
                                </div>
                              )}

                              {fmNeedsFiled && (!fmItemExists || !fmHasUpload) && (
                                <div className="text-xs text-gray-600 mt-1">
                                  {fmItemExists ? 'No upload yet for certificate.' : 'Checklist item missing.'}
                                </div>
                              )}
                            </div>
                                                      {(() => {
                            const paid = Number(c.filing_fees_paid || 0);
                            const receipted = Number(c.filing_fees_receipted || 0);
                            const feeTotal = Number(c.filing_fee_amount || 0);
                            const dueToCourt = Math.max(0, paid - receipted);
                            const needsCourtPayment = dueToCourt > 0.009; // tolerate tiny rounding

                            const money = (n) => {
                              try {
                                return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                              } catch (_) {
                                return String(n);
                              }
                            };

                            return (
                              <div className="mt-2 space-y-1">
                                <div className="text-sm text-gray-700">
                                  Filing Fees Paid: ${money(paid)} / Total Required: ${money(feeTotal)}
                                </div>
                                <div className="text-sm text-gray-700 flex items-center gap-2">
                                  <span>Filing Fees Receipted: ${money(receipted)}</span>
                                  {needsCourtPayment && (
                                    <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800 ring-1 ring-inset ring-red-200">
                                      Pay Court: ${money(dueToCourt)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                          </div>
                          {/* Second column: Important Dates card */}
                          <div
                            className={`
                              mt-2 p-4 rounded
                              ${!c.trustee_docs ? 'bg-red-100 border-l-4 border-red-500'
                                :'bg-green-100 border-l-4 border-green-500'}
                            `}>
                            <h4 className="font-medium text-indigo-700">Important Dates</h4>
                            
                            <div className={`mt-1 text-sm ${sortKey === 'date_filed' ? 'font-bold text-indigo-700' : 'text-gray-700'}`}>
                              Date Filed: {c.date_filed
                              ? parseLocalDate(c.date_filed)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                              : 'TBD'}
                            </div>
                            <div className={`mt-1 text-sm ${sortKey === 'meeting_date' ? 'font-bold text-indigo-700' : 'text-gray-700'}`}>
                              Meeting: {c.meeting_date ? (() => {
                                const dt = new Date(c.meeting_date);
                                // Adjust so the UTC timestamp is treated as local
                                dt.setMinutes(dt.getMinutes());
                                return dt.toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: 'numeric',
                                  minute: 'numeric',
                                  hour12: true
                                });
                              })() : 'TBD'}
                            </div>
                            {c.chapter === '7' ? (
                              <div className={`mt-1 text-sm ${sortKey === 'reaffirmation_deadline' ? 'font-bold text-indigo-700' : 'text-gray-700'}`}>
                                Reaffirmation Deadline: {c.reaffirmation_deadline
                                ? parseLocalDate(c.reaffirmation_deadline)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : 'TBD'}
                              </div>
                            ) : (
                              <div className={`mt-1 text-sm ${sortKey === 'confirmation_date' ? 'font-bold text-indigo-700' : 'text-gray-700'}`}>
                                Confirmation Date: {c.confirmation_date
                                ? parseLocalDate(c.confirmation_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : 'TBD'}
                              </div>
                            )}
                            <div className={`mt-1 text-sm ${sortKey === 'financial_management_deadline' ? 'font-bold text-indigo-700' : 'text-gray-700'}`}>
                              <span className={fmPastDue ? 'font-bold text-red-700' : ''}>
                                Claims/Financial Management Deadline: {c.financial_management_deadline
                                ? parseLocalDate(c.financial_management_deadline)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : 'TBD'}
                              </span>
                              {fmPastDue && (
                                <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800 ring-1 ring-inset ring-red-200">
                                  Past due
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
<button
  type="button"
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
    handleDeleteCase(c.case_id);
  }}
  className="absolute top-2 right-2 z-10 inline-flex items-center justify-center rounded-md p-2 text-red-600 hover:bg-red-50"
  title="Delete case"
>
  <span className="sr-only">Delete</span>
  <TrashIcon className="h-5 w-5" />
</button>
                    </li>
                    );
                  })}
                </ul>
              </div>
            );
          }
          return (
            <div key={chap} className="mt-4">
              <h4 className="text-md font-semibold mb-2">Chapter {chap}</h4>
              <ul className="space-y-4">
                {chapterCases.map((c) => {
                  const primaryDebtor = getPrimaryDebtor(c.debtors, { filingOnly: true });
                  const needed = Number(c.document_summary?.required_missing || 0);
                  const awaiting = Number(c.document_summary?.awaiting_review || 0);
                  const accepted = Number(c.document_summary?.accepted || 0);
                  const hasAwaiting = awaiting > 0;
                  const allAccepted = needed === 0 && awaiting === 0;
                  return (
                    <li
                      key={c.case_id}
                      className={`relative group py-4 px-4 border border-gray-200 rounded-lg flex justify-between items-center transition-transform duration-200 ease-in-out hover:scale-105 hover:bg-gray-50 cursor-pointer ${
                        deletingCaseIds.includes(c.case_id) ? 'opacity-50 pointer-events-none' : ''
                      }`}
                    >
                      {/* Full-card overlay link enables middle/cmd/ctrl click */}
                      <a
                        href={`/case/${c.case_id}`}
                        className="absolute inset-0 z-0"
                        aria-label={`Open case ${c.case_id}`}
                        onClick={(e) => {
                          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                          e.preventDefault();
                          navigate(`/case/${c.case_id}`);
                        }}
                      />
                      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* First column: main info */}
                        <div className="flex flex-col">
                          {deletingCaseIds.includes(c.case_id) && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                              <svg
                                className="animate-spin h-6 w-6 text-gray-600"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z"
                                ></path>
                              </svg>
                            </div>
                          )}
                          <div className="font-semibold text-gray-900">
                            {primaryDebtor
                              ? `${primaryDebtor.first_name} ${primaryDebtor.second_name ? primaryDebtor.second_name + ' ' : ''}${primaryDebtor.last_name}`
                              : 'Unnamed Client'}
                          </div>
                          <div className="text-sm text-gray-600">
                            Case ID: {c.case_id}
                          </div>
                          <div className="text-sm text-gray-600">
                            Email: {primaryDebtor?.email || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-600">
                            Phone: {primaryDebtor?.phone || 'N/A'}
                          </div>
                          {status === 'prospect' && (
                            <>
                              <div className="text-sm text-gray-700">
                                Created: {new Date(c.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true })}
                              </div>
                              <div className="text-sm text-gray-700">
                                Fees Paid: ${c.fees_paid_prior} / Pre-Filing Fees: ${c.pre_filing_fees}
                              </div>
                            </>
                          )}
                          {status === 'retained' && (
                            <div className="text-sm text-gray-700 mt-2">
                              Fees Paid: ${c.fees_paid_prior} / Pre-Filing Fees: ${c.pre_filing_fees}
                            </div>
                          )}
                        </div>
                        {/* Second column: document overview for retained only */}
                        {status === 'retained' ? (
                          <div
                            className={`
                              mt-2 p-4 rounded
                              ${hasAwaiting ? 'bg-red-100 border-l-4 border-red-500'
                                : allAccepted ? 'bg-green-100 border-l-4 border-green-500'
                                : 'bg-gray-50 border-l-4 border-indigo-500'}
                            `}
                          >
                            <h4 className="font-medium text-indigo-700">Document Overview</h4>
                            <div className="mt-1 text-sm text-gray-700">
                              Needed: <span className="font-semibold">{needed}</span>,{' '}
                              Awaiting Review: <span className={awaiting > 0 ? 'font-bold text-red-600' : ''}>{awaiting}</span>,{' '}
                              Accepted: <span className="font-semibold">{accepted}</span>
                            </div>
                          </div>
                        ) : (
                          <div></div>
                        )}
                      </div>
<button
  type="button"
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
    handleDeleteCase(c.case_id);
  }}
  className="absolute top-2 right-2 z-10 inline-flex items-center justify-center rounded-md p-2 text-red-600 hover:bg-red-50"
  title="Delete case"
>
  <span className="sr-only">Delete</span>
  <TrashIcon className="h-5 w-5" />
</button>
                        </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <DashboardShell>
      {successMessage && (
        <div className="fixed top-5 right-5 z-50">
          <div className="animate-fade-in-out px-4 py-3 rounded-md bg-green-50 border border-green-400 text-green-700 shadow-lg">
            {successMessage}
          </div>
        </div>
      )}
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap gap-2 md:gap-4">
          {tabs.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={classNames(
                activeTab === tab ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700',
                'pb-2 border-b-2 font-medium'
              )}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
          {/* Filters */}
          {activeTab === 'filed' && (
            <div className="w-full md:w-auto">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 md:p-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  <label className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={filedFilters.courtFeesDue}
                      onChange={(e) => setFiledFilters(prev => ({ ...prev, courtFeesDue: e.target.checked }))}
                    />
                    <span className="whitespace-nowrap">Court fees due</span>
                  </label>

                  <label className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={filedFilters.fmNeedsUploaded}
                      onChange={(e) => setFiledFilters(prev => ({ ...prev, fmNeedsUploaded: e.target.checked }))}
                    />
                    <span className="whitespace-nowrap">FM needs upload</span>
                  </label>

                  <label className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={filedFilters.fmNeedsFiled}
                      onChange={(e) => setFiledFilters(prev => ({ ...prev, fmNeedsFiled: e.target.checked }))}
                    />
                    <span className="whitespace-nowrap">FM needs filed</span>
                  </label>

                  <label className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={filedFilters.fmDeadlinePast}
                      onChange={(e) => setFiledFilters(prev => ({ ...prev, fmDeadlinePast: e.target.checked }))}
                    />
                    <span className="whitespace-nowrap">FM deadline past</span>
                  </label>

                  <label className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={filedFilters.trusteeDocsMissing}
                      onChange={(e) => setFiledFilters(prev => ({ ...prev, trusteeDocsMissing: e.target.checked }))}
                    />
                    <span className="whitespace-nowrap">Trustee docs missing</span>
                  </label>

                  <button
                    type="button"
                    onClick={() => {setFiledFilters({
                      courtFeesDue: false,
                      fmNeedsUploaded: false,
                      fmNeedsFiled: false,
                      fmDeadlinePast: false,
                      trusteeDocsMissing: false,
                    });
                  setChapterFilter('all');}}
                    className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100"
                    title="Clear filed filters"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'retained' && (
            <div className="w-full md:w-auto">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 md:p-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={retainedFilters.needsReview}
                      onChange={(e) => setRetainedFilters(prev => ({ ...prev, needsReview: e.target.checked }))}
                    />
                    <span className="whitespace-nowrap">Needs review</span>
                  </label>

                  <button
                    type="button"
                    onClick={() => setRetainedFilters({ needsReview: false })}
                    className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100"
                    title="Clear retained filters"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Sort */}
          <div className="flex items-center w-full md:w-auto flex-wrap gap-2">
            <select
              value={sortKey}
              onChange={e => setSortKey(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-2 text-sm w-full md:w-auto"
            >
              <option value="created_at">Created Date</option>
              <option value="updated_at">Updated Date</option>
              <option value="meeting_date">Meeting Date</option>
              <option value="reaffirmation_deadline">Reaffirmation Deadline</option>
              <option value="date_filed">Date Filed</option>
              <option value="financial_management_deadline">Claims Deadline</option>
            </select>
            <select
  value={chapterFilter}
  onChange={(e) => setChapterFilter(e.target.value)}
  className="border border-gray-300 rounded-md px-2 py-2 text-sm w-full md:w-auto"
>
  <option value="all">All Chapters</option>
  <option value="7">Chapter 7</option>
  <option value="13">Chapter 13</option>
</select>
            <button
              onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
              className="ml-2 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50"
            >
              {sortDirection === 'asc' ? '↓' : '↑' }
            </button>
          </div>
        </div>
      </div>
      <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
  <div className="text-sm text-gray-700">
    <span className="font-semibold">Active filters:</span>{' '}
    {(() => {
      const labels = getActiveFilterLabels();
      if (!labels.length) return <span className="text-gray-500">None</span>;
      return (
        <span className="inline-flex flex-wrap gap-2">
          {labels.map(l => (
            <span
              key={l}
              className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700 ring-1 ring-inset ring-indigo-200"
            >
              {l}
            </span>
          ))}
        </span>
      );
    })()}
  </div>

  <div className="text-sm text-gray-700">
    <span className="font-semibold">Sort:</span>{' '}
    <span className="text-gray-600">
      {SORT_LABELS[sortKey] || sortKey}
    </span>{' '}
    <span className="text-gray-500">
      ({sortDirection === 'asc' ? 'Oldest → Newest' : 'Newest → Oldest'})
    </span>
  </div>
</div>
      {loadingCases ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-600 shadow-sm">
          Loading cases…
        </div>
      ) : loadError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {loadError}
        </div>
      ) : (
        renderCases()
      )}
    </DashboardShell>
  );
}
